import { useEffectOnce } from '@ui-common/hooks/useEffectOnce'
import { ErrorAndDescriptionRow } from '@ui-components/common/ErrorAndDescriptionRow'
import { AdditionalText } from '@ui-components/typography/Typography'
import { ChangeEvent, DragEvent, FocusEventHandler, useRef, useState } from 'react'
import { styled } from 'styled-components'

import { Button } from '../actions/Button'
import { Label } from '../dataDisplay/Label'
import { Colors } from '../global/colors'
import { SPACE_XXXSMALL } from '../global/spacing'

const DropzoneContainer = styled.div<{ $isDraggedOver: boolean; $error: boolean }>`
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  border: ${(props) => (props.$error ? `${Colors.BorderError}` : `${Colors.BorderStrongNeutral}`)} 1px
    ${(props) => (props.$isDraggedOver ? 'solid' : props.$error ? 'solid' : 'dashed')};
  border-radius: ${SPACE_XXXSMALL};
  background-color: ${(props) =>
    props.$isDraggedOver ? `${Colors.BackgroundWeakNeutral}` : `${Colors.BackgroundWeakestNeutral}`};
  padding: 24px 0;
`

const UploadButtonDescription = styled(AdditionalText)<{ $isDanger: boolean }>`
  margin-top: ${SPACE_XXXSMALL};
  text-align: center;
  color: ${(props) => (props.$isDanger ? Colors.TextError : Colors.TextStrongNeutral)};
`

const StyledLabel = styled(Label)`
  > div {
    margin-bottom: ${SPACE_XXXSMALL};
  }
`

interface FormUploadProps {
  onDrop: (event: DragEvent<HTMLDivElement>) => void
  onChange: (event: ChangeEvent<HTMLInputElement>) => void
  uploadButtonTitle: string
  uploadButtonDescription: string
  dropzoneTitle: string
  dropzoneDescription?: string
  accept: string | undefined
  isDanger?: boolean
  isDisabled?: boolean
  isFileLimitReached?: boolean
  onFileLimitReached?: () => void
  required?: boolean
  error?: string
  onBlur?: FocusEventHandler
  name?: string
  allowMultipleFiles?: boolean
}

export const FormUpload = ({
  onDrop,
  onChange,
  uploadButtonTitle,
  uploadButtonDescription,
  dropzoneTitle,
  dropzoneDescription,
  accept,
  isDanger = false,
  isDisabled = false,
  isFileLimitReached,
  onFileLimitReached,
  required = false,
  error,
  onBlur,
  name,
  allowMultipleFiles = true,
}: FormUploadProps) => {
  const inputRef = useRef<HTMLInputElement>(null)
  const dropzoneRef = useRef<HTMLDivElement>(null)
  const [isDraggedOver, setIsDraggedOver] = useState(false)

  useEffectOnce(() => {
    const prevent = (e: WindowEventMap['dragover'] | WindowEventMap['drop']) => {
      e.preventDefault()
    }

    document.addEventListener('dragover', prevent, { capture: true })
    document.addEventListener('drop', prevent, { capture: true })

    return () => {
      document.removeEventListener('dragover', prevent, { capture: true })
      document.removeEventListener('drop', prevent, { capture: true })
    }
  })

  const onDragOver = (event: DragEvent<HTMLDivElement>) => {
    // Prevent file from being opened when dragged in
    event.preventDefault()
  }

  const onDragLeave = (event: DragEvent<HTMLDivElement>) => {
    const relatedTarget = event.relatedTarget as HTMLElement | null
    const parentDiv = event.currentTarget

    if (!parentDiv.contains(relatedTarget)) {
      setIsDraggedOver(false)
    }
  }

  return (
    <>
      <StyledLabel isRequired={required} id={'form-upload'} label={dropzoneTitle} description={dropzoneDescription} />
      <DropzoneContainer
        id="drop-zone"
        data-testid="drop-zone"
        onDrop={(e) => {
          // Prevent file from being opened when dropped into the zone
          e.preventDefault()
          setIsDraggedOver(false)
          if (isFileLimitReached) {
            onFileLimitReached?.()
            return
          }
          onDrop(e)
        }}
        $isDraggedOver={isDraggedOver}
        $error={!!error}
        ref={dropzoneRef}
        onDragOver={onDragOver}
        onDragEnter={() => setIsDraggedOver(true)}
        onDragLeave={onDragLeave}
        onBlur={onBlur}
      >
        <input
          type="file"
          id="files"
          data-testid="files"
          hidden={true}
          multiple={allowMultipleFiles}
          onChange={onChange}
          disabled={isDisabled}
          accept={accept}
          value=""
          name={name}
          ref={inputRef}
        />
        <Button
          onClick={(e) => {
            if (e.currentTarget.ariaDisabled === 'true') {
              return
            }
            if (isFileLimitReached) {
              onFileLimitReached?.()
              return
            }
            if (inputRef?.current) {
              inputRef.current.click()
            }
          }}
          aria-disabled={isDisabled}
          disabled={isDisabled}
          aria-describedby="upload-button-description"
          label={uploadButtonTitle}
          variant="secondary"
        />
        <UploadButtonDescription
          tagName="span"
          $isDanger={isDanger}
          id={'upload-button-description'}
          data-testid="upload-button-description"
        >
          {uploadButtonDescription}
        </UploadButtonDescription>
      </DropzoneContainer>
      <ErrorAndDescriptionRow error={error} id={'form-upload'} />
    </>
  )
}
